import { SamplePack } from '@arpeggi/shared'
import { Box, IconButton, Icons } from '@arpeggi/ui'
import { observer } from 'mobx-react-lite'
import { ComponentProps, FC } from 'react'
import { DotsVertical, TwitterLogo } from '../../../__legacy__/components/core/icons'
import { Share } from '../../../__legacy__/components/core/icons/responsive-icons'
import withTooltip from '../../../__legacy__/components/core/withTooltip'
import * as Dropdown from '../../../__legacy__/components/platform/new-explore/components/Dropdown'
import { IconWrapper } from '../../../__legacy__/components/platform/new-explore/components/TableRow'
import { RegisterSongModal } from '../../../__legacy__/components/studio/mint/register-song-modal'
import { useMediaQuery } from '../../../__legacy__/hooks/useMediaQuery'
import { useSPActions } from '../hooks'
import { Actions } from '../primitives'
import { DownloadSoundsModal } from './DownloadSoundsModal'
import { ShareIconButton } from './ShareIconButton'

const TooltipWrapper = withTooltip(Box)

type RegisterSongTriggerProps = { disabled: boolean } & ComponentProps<typeof Actions.MainAction>

const RegisterSongTrigger: FC<RegisterSongTriggerProps> = ({ disabled, ...props }) => {
  const prefixIcon = <Icons.Upload height="12px" width="12px" />
  if (disabled) {
    return (
      <TooltipWrapper tooltipText="Collect a token to register your song">
        <Actions.MainAction prefixIcon={prefixIcon} disabled={disabled}>
          Upload track
        </Actions.MainAction>
      </TooltipWrapper>
    )
  }
  return (
    <Actions.MainAction prefixIcon={prefixIcon} {...props}>
      Upload track
    </Actions.MainAction>
  )
}

const SPActionsMobile: FC<{ sp: SamplePack }> = observer(({ sp }) => {
  const {
    isDownloadModalOpen,
    isUploadModalOpen,
    setIsDownloadModalOpen,
    setIsUploadModalOpen,
    showUploadDownload,
    hasProject,
    tweet,
    uploadDisabled,
    handleCopy,
    soundsQuery,
    sounds
  } = useSPActions(sp)

  return (
    <>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <IconButton variant="secondary" css={{ padding: 0 }} size="md" icon={<DotsVertical />} />
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content side="bottom" sideOffset={4}>
            <Dropdown.Item onClick={tweet}>
              <IconWrapper>
                <TwitterLogo />
              </IconWrapper>
              Tweet
            </Dropdown.Item>
            <Dropdown.Item onClick={handleCopy}>
              <IconWrapper>
                <Share />
              </IconWrapper>
              Share
            </Dropdown.Item>
            {hasProject && (
              <Dropdown.Item onClick={() => window.open(sp.project.url, '_blank')}>
                <IconWrapper>
                  <Icons.Details />
                </IconWrapper>
                {sp.project.name}
              </Dropdown.Item>
            )}
            {showUploadDownload && !soundsQuery.isLoading && (
              <>
                <Dropdown.Item onClick={() => setIsDownloadModalOpen(true)}>
                  <IconWrapper>
                    <Icons.Download height="16px" width="16px" />
                  </IconWrapper>
                  Download samples
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setIsUploadModalOpen(true)} disabled={uploadDisabled}>
                  <IconWrapper>
                    <Icons.Upload height="12px" width="12px" />
                  </IconWrapper>
                  Upload track
                </Dropdown.Item>
              </>
            )}
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      <DownloadSoundsModal
        samplePackId={sp.id}
        zipFileName={`${sp.title}.zip`}
        open={isDownloadModalOpen}
        onOpenChange={setIsDownloadModalOpen}
        isControlled
      />
      <RegisterSongModal
        open={isUploadModalOpen}
        onOpenChange={setIsUploadModalOpen}
        remixOfSamplePack={sp}
        sounds={sounds}
      />
    </>
  )
})

const SPActionsDesktop: FC<{ sp: SamplePack }> = observer(({ sp }) => {
  const {
    isDownloadModalOpen,
    isUploadModalOpen,
    setIsDownloadModalOpen,
    setIsUploadModalOpen,
    showUploadDownload,
    hasProject,
    tweet,
    uploadDisabled,
    samplePackLink,
    sounds
  } = useSPActions(sp)

  return (
    <Actions.Root>
      {showUploadDownload && (
        <>
          <ShareIconButton textToCopy={samplePackLink} />
          {hasProject && (
            <a href={sp.project.url} target="_blank">
              <Actions.SecondaryAction variant="secondary">
                {sp.project.name}
              </Actions.SecondaryAction>
            </a>
          )}
          <DownloadSoundsModal
            samplePackId={sp.id}
            zipFileName={`${sp.title}.zip`}
            open={isDownloadModalOpen}
            onOpenChange={setIsDownloadModalOpen}
          />
          <RegisterSongModal
            open={isUploadModalOpen}
            onOpenChange={setIsUploadModalOpen}
            trigger={<RegisterSongTrigger disabled={uploadDisabled} />}
            remixOfSamplePack={sp}
            sounds={sounds}
          />
        </>
      )}
      <Actions.SecondaryAction onClick={tweet} variant="secondary" prefixIcon={<TwitterLogo />}>
        Tweet
      </Actions.SecondaryAction>
    </Actions.Root>
  )
})

export const SPActions: FC<{ sp: SamplePack }> = observer(({ sp }) => {
  const isLessThanTabletScreen = useMediaQuery(1200)

  if (isLessThanTabletScreen) {
    return <SPActionsMobile sp={sp} />
  }

  return <SPActionsDesktop sp={sp} />
})
